import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountdown } from '../hooks/useCountdown.js';
import Modal from './Modal.js';
import { paymentExpiredRollbackByReservationId } from '../../openapi/orders/orders.js';
import { useGetOrdersByReservationId } from '../../openapi/reservations/reservations.js';

const TimeLimit = ({
    onExpire,
    reservationId,
    reservationHash,
}: {
    onExpire?: () => void;
    reservationId: number;
    reservationHash: string;
}) => {
    const { targetTime } = useCountdown();
    const [remainingTime, setRemainingTime] = useState<number>(1);
    const [showModal, setShowModal] = useState(false);
    const { t } = useTranslation();
    const { data: dataOrders } = useGetOrdersByReservationId(reservationId, {
        reservationHash,
    });

    const rollback = useCallback(() => {
        const pendingOrder = dataOrders?.orders?.find(
            (order) => (order.status as unknown as string) === 'pending' && order.rollbackTimeAt,
        );
        if (pendingOrder) {
            paymentExpiredRollbackByReservationId(reservationId, {
                reservationHash,
            });
        }
    }, [dataOrders, reservationId, reservationHash]);

    useEffect(() => {
        if (targetTime === null) {
            return;
        }

        if (remainingTime <= 0) {
            rollback();
            setShowModal(true);
            return;
        }
        setRemainingTime(
            Math.floor((new Date(targetTime).getTime() - new Date().getTime()) / 1000),
        );
        const interval = setInterval(() => {
            setRemainingTime(
                Math.floor((new Date(targetTime).getTime() - new Date().getTime()) / 1000),
            );
        }, 1000);

        return () => clearInterval(interval);
    }, [remainingTime, targetTime, setShowModal, dataOrders, rollback]);

    const minutes = String(Math.floor(remainingTime / 60)).padStart(2, '0');
    const seconds = String(remainingTime % 60).padStart(2, '0');

    if (targetTime === null) {
        return null;
    }

    return (
        <div className={`fireknife-time-limit`}>
            {remainingTime > 0 && (
                <>
                    <div className="fireknife-time-limit__label">
                        {t('widgetInformations.paymentDeadlineExpires')}:
                    </div>
                    <div className="fireknife-time-limit__value">
                        {minutes}:{seconds}
                    </div>
                </>
            )}
            {showModal && (
                <Modal
                    title={t('widgetInformations.paymentExpiredTitle')}
                    onClose={() => setShowModal(false)}
                    actions={[
                        {
                            label: t('widgetInformations.restartBooking'),
                            onClick: () => {
                                onExpire?.();
                                setShowModal(false);
                            },
                            variant: 'fireknife-btn--primary',
                        },
                    ]}
                >
                    <p>{t('widgetInformations.paymentExpired')}</p>
                </Modal>
            )}
        </div>
    );
};

export default TimeLimit;
